import { deleteItems, readItems } from '@directus/sdk';
import { useMutation, useQuery } from '@tanstack/react-query';

import type { Schema } from '../../../../types/DirectusSchema';
import { eventMapper } from '../../mappers/event';
import { directusClient } from '../../services/directusClient/directusClient.ts';
import { tanstackQueryClient } from '../../services/tanstackQueryClient.ts';

export const useEventItems = ({
  queryActive = true,
  filters,
}: {
  queryActive?: boolean;
  filters?: { eventGroupId?: string };
}) => {
  const query = useQuery({
    staleTime: 60 * (60 * 1000), // 60 mins
    queryKey: ['events', { filters }, 'to-form'],
    enabled: queryActive,
    queryFn: async () => {
      const data = await directusClient.request(
        readItems('events', {
          filter: {
            events_group: {
              _eq: filters?.eventGroupId,
            },
          },
          fields: [
            '*',
            {
              venue: ['id', 'commercial_name'],
              category: ['id', 'title'],
              target_audiences: ['*'],
            },
          ],
        }),
      );

      return data.map((e) => eventMapper.apiToForm(e as unknown as Schema['events'][0]));
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      if (!ids) {
        return;
      }

      await directusClient.request(deleteItems('events', ids));

      return ids;
    },
    onSuccess: async (eventIds) => {
      if (!eventIds) {
        return;
      }

      eventIds.forEach((eventId) => {
        tanstackQueryClient.removeQueries({
          queryKey: ['events', { eventId }],
        });
      });

      await tanstackQueryClient.invalidateQueries({
        queryKey: ['events'],
        exact: false,
      });
    },
  });

  return { query, removeMutation };
};
